import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';

const Wrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
`;

const Card = styled.div`
  display: flex;
  gap: 24px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const AvatarImage = styled.img`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid #e5e7eb;
`;

const Info = styled.div`
  flex: 1;
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`;

const Username = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
`;

const Grid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 16px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 2px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: #374151;
`;

const EmptyValue = styled.span`
  color: #9ca3af;
  font-style: italic;
`;

export default function Profile() {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('/students/me')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || {};
        setStudent(data.student || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch student profile', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderValue = (value) => {
    if (!value) {
      return <EmptyValue>Not provided</EmptyValue>;
    }
    return <FieldValue>{value}</FieldValue>;
  };

  return (
    <StudentLayout title="Profile">
      <Wrapper>
        <Heading>Your Profile</Heading>
        <Subtitle>View the personal details associated with your student account.</Subtitle>

        {loading && <Loader message="Loading your profile…" />}

        {!loading && student && (
          <Card>
            {student.imagePath ? (
              <AvatarImage
                src={getImageURL(student.imagePath)}
                alt={student.fullName || 'Student profile'}
              />
            ) : null}
            <Info>
              <Name>{student.fullName || 'Student'}</Name>
              <Username>Username: {student.username || '—'}</Username>

              <Grid>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  {renderValue(student.email)}
                </div>
                <div>
                  <FieldLabel>Telephone</FieldLabel>
                  {renderValue(student.telephone)}
                </div>
                <div>
                  <FieldLabel>Date of birth</FieldLabel>
                  {renderValue(formatDate(student.dob))}
                </div>
                <div>
                  <FieldLabel>Gender</FieldLabel>
                  {renderValue(student.gender)}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <FieldLabel>NIC number</FieldLabel>
                  {renderValue(student.nicNumber)}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <FieldLabel>Address</FieldLabel>
                  {renderValue(student.address)}
                </div>
              </Grid>
            </Info>
          </Card>
        )}
      </Wrapper>
    </StudentLayout>
  );
}
